import React, { memo, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { Button, DynamicIconWrapper, Icons, Tooltip } from '@components/common';
import CustomPolygonControls from '@components/Map/MapControls/Controls/CustomPolygonControls';
import CustomPolygonList from '@components/Map/MapControls/Controls/CustomPolygonList';
import GeneralFilters, { EFilterType } from '@components/Map/MapControls/Controls/Filters/GeneralFilters';
import Sectors from '@components/Map/MapControls/Controls/Filters/Sectors';
import FiltersOokla from '@components/Map/MapControls/Controls/FiltersOokla';
import MapSettings from '@components/Map/MapControls/Controls/MapSettings';

import { useQueryDatesOokla } from '@hooks/ookla/useQueryDatesOokla';
import { useQueryAssetOwnersForFilter } from '@hooks/organisation/useQueryAssetOwnersForFilter';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';

import { auth, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	clearSelectedSiteIdsToRenderSector,
	resetRightSideControls,
	setMapMode,
	setPinnedFilter,
	setRightSideControls,
} from '@slices/map';

import { isEmpty, normalizeObject } from '@utils/common';
import { stopPropagation } from '@utils/stopPropagation';
import { getTooltipContent } from '@components/Map/MapControls/Controls/helpers';

import { EControlsType, TEControlsType } from '@enums/common';
import { EDataType, EMapMode, TMapMode } from '@enums/map';

import styles from './MapControls.module.scss';

const RightSideControls = () => {
	const dispatch = useAppDispatch();

	const {
		mapMode,
		rightSideControls: controlsState,
		appliedAssetsFilterValues: {
			ownerId,
			...filters
		},
		appliedSitesFiltersValues,
		isAcquiredAssetsShownApplied,
		showingCustomPolygonIds,
		areBoroughsEnabled,
		markerType,
		pinnedFilter,
		isMapLoaded,
		sectors: { isSectorsShown, selectedSiteIds: selectiveSiteSectors },
	} = useAppSelector(map);
	const { accessPermission } = useAppSelector(auth);

	const { prefetchAssetOwnersForFilter } = useQueryAssetOwnersForFilter();

	const { datesOokla } = useQueryDatesOokla();

	const isAssetsMode = markerType === EDataType.assets;
	const { vendors: allVendors } = useManageSitesOptions(markerType === EDataType.sites);

	const { vendor: appliedVendorFilters, ...appliedSitesFilters } = appliedSitesFiltersValues;

	const isFiltersApplied = isAssetsMode
		? !isEmpty(normalizeObject(filters, { removeFalsyBoolean: true })) || isAcquiredAssetsShownApplied
		: !isEmpty(normalizeObject(appliedSitesFilters, { removeFalsyBoolean: true }));
	const isAssetOwnersFilterApplied = ownerId && ownerId.length > 0;
	const isSelectiveSectorsRendered = selectiveSiteSectors.length > 0;

	const [isOwnersFiltersApplied, setIsOwnersFiltersApplied] = useState(false);

	useEffect(() => {
		if (isAssetsMode) {
			setIsOwnersFiltersApplied(!!isAssetOwnersFilterApplied);
		} else if (allVendors) {
			setIsOwnersFiltersApplied(appliedVendorFilters.length !== allVendors.length);
		}
	}, [allVendors, appliedVendorFilters.length, isAssetOwnersFilterApplied, isAssetsMode]);

	const {
		assetOwnerOrganizationAdministrator,
		networkOperatorAssetEnquirer,
		networkOperatorAssetViewer,
		networkOperatorOrganizationAdministrator,
		platformOwnerAdministrator,
		assetOwnerAttributeUpdater,
		networkOperatorKpiUpdater,
		networkOperatorKpiViewer,
		networkOperatorTelecomSitesViewer,
		networkOperatorTelecomSitesAttributeUpdater,
		assetConsumerOrganizationAdministrator,
		assetConsumerAssetEnquirer,
		assetConsumerAssetViewer,
	} = accessPermission;

	const isAddAssetButtonAvailable = (
		assetOwnerOrganizationAdministrator ||
		assetOwnerAttributeUpdater
	) && isAssetsMode;

	const isOwnersOrVendorsFilterAvailable = (
		platformOwnerAdministrator ||
		networkOperatorAssetEnquirer ||
		networkOperatorAssetViewer ||
		networkOperatorOrganizationAdministrator ||
		networkOperatorKpiUpdater ||
		networkOperatorTelecomSitesAttributeUpdater ||
		networkOperatorTelecomSitesViewer ||
		assetConsumerOrganizationAdministrator ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	const isKpiView = (
		networkOperatorKpiUpdater ||
		networkOperatorKpiViewer ||
		assetConsumerOrganizationAdministrator
	) && !isAssetsMode;

	const toggleMapControlView = useCallback((e: any, name: TEControlsType) => {
		stopPropagation(e);

		// If there is no data-id attribute on the target element, the function will not execute
		// The data-id attribute is necessary to catch the bubbling event and ensure proper functioning
		if (!e.target.dataset.id || (pinnedFilter && pinnedFilter !== name)) return;

		if (name === EControlsType.createAssetMode) {
			if (mapMode === EMapMode.addMarker) dispatch(setMapMode(EMapMode.view));
			else dispatch(setMapMode(EMapMode.addMarker));
		}

		dispatch(setRightSideControls(name));
		dispatch(setPinnedFilter(null));
	}, [dispatch, mapMode, pinnedFilter]);

	const tooltips = {
		[EControlsType.createAssetMode]: getTooltipContent({ name: EControlsType.createAssetMode }),
		[EControlsType.filtersOokla]: getTooltipContent({ name: EControlsType.filtersOokla }),
		[EControlsType.customPolygonControls]: getTooltipContent({
			name: EControlsType.customPolygonControls,
			areBoroughsEnabled,
			pinnedFilter,
			isSectorsShown,
		}),
		[EControlsType.customPolygonList]: getTooltipContent({
			name: EControlsType.customPolygonList,
			areBoroughsEnabled,
			pinnedFilter,
			isSectorsShown,
		}),
		[EControlsType.filters]: getTooltipContent({ name: EControlsType.filters, isAssetsMode, pinnedFilter }),
		[EControlsType.owners]: getTooltipContent({ name: EControlsType.owners, isAssetsMode, pinnedFilter }),
		[EControlsType.mapSettings]: getTooltipContent({ name: EControlsType.mapSettings, pinnedFilter }),
		[EControlsType.dashboardKpi]: getTooltipContent({ name: EControlsType.dashboardKpi, pinnedFilter }),
		[EControlsType.sectors]: getTooltipContent({ name: EControlsType.sectors, pinnedFilter }),
	};

	useEffect(() => {
		if (mapMode === EMapMode.view) dispatch(resetRightSideControls());
	}, [dispatch, mapMode]);

	const toggleMapMode = (mode: TMapMode) => () => dispatch(setMapMode(mode));

	const isCustomPolygonControlDisabled = (
		areBoroughsEnabled ||
		isSectorsShown ||
		(pinnedFilter && pinnedFilter !== EControlsType.customPolygonControls)
	);

	const handleHideAllSectors = useCallback(() => {
		dispatch(clearSelectedSiteIdsToRenderSector());
	}, [dispatch]);

	const handleMapControlView = useCallback((mapMode: TEControlsType) => (e: React.MouseEvent<HTMLDivElement>) => {
		if (
			isCustomPolygonControlDisabled &&
			(mapMode === EControlsType.customPolygonControls || mapMode === EControlsType.customPolygonList)
		) return;

		toggleMapControlView(e, mapMode);
	}, [isCustomPolygonControlDisabled, toggleMapControlView]);

	return (
		isMapLoaded ? (
			<div className={styles.controlsWrapper}>
				{isSelectiveSectorsRendered && (
					<Button
						className={styles.overlayButton}
						containerClassName={styles.overlayButtonContainer}
						fullWidth
						onClick={handleHideAllSectors}
						text='Hide all sectors'
					/>
				)}
				<div className={styles.controlBlock}>
					{isAddAssetButtonAvailable && (
						<Tooltip
							content={tooltips[EControlsType.createAssetMode]}
							isDisabled={controlsState[EControlsType.createAssetMode]}
							noWordBreak
						>
							<div
								data-id='icon-button'
								onClick={handleMapControlView(EControlsType.createAssetMode)}
							>
								<DynamicIconWrapper
									data-id='icon-button'
									icon={Icons.plus}
									isActive={controlsState[EControlsType.createAssetMode]}
								/>
							</div>
						</Tooltip>
					)}
					<Tooltip content='Hand'>
						<div
							data-id='icon-button'
							onClick={toggleMapMode(EMapMode.view)}
						>
							<DynamicIconWrapper
								data-id='icon-button'
								icon={Icons.hand}
								isActive={mapMode === EMapMode.view}
							/>
						</div>
					</Tooltip>
					<Tooltip
						content={tooltips[EControlsType.customPolygonControls]}
						interactive
						isDisabled={controlsState[EControlsType.customPolygonControls]}
						noWordBreak
					>
						<div
							data-id='icon-button'
							onClick={handleMapControlView(EControlsType.customPolygonControls)}
						>
							<DynamicIconWrapper
								data-id='icon-button'
								icon={Icons.polygon}
								isActive={controlsState[EControlsType.customPolygonControls]}
								isDisabled={!!isCustomPolygonControlDisabled}
							/>
							{controlsState[EControlsType.customPolygonControls] && <CustomPolygonControls />}
						</div>
					</Tooltip>
					<Tooltip
						content={tooltips[EControlsType.customPolygonList]}
						interactive
						isDisabled={controlsState[EControlsType.customPolygonList]}
						noWordBreak
					>
						<div
							data-id='icon-button'
							onClick={handleMapControlView(EControlsType.customPolygonList)}
						>
							<DynamicIconWrapper
								data-id='icon-button'
								icon={Icons.layer}
								isActive={
									controlsState[EControlsType.customPolygonList] ||
									!!showingCustomPolygonIds.length
								}
								isDisabled={!!isCustomPolygonControlDisabled}
							/>
							{controlsState[EControlsType.customPolygonList] && <CustomPolygonList />}
						</div>
					</Tooltip>
				</div>
				{!isEmpty(datesOokla) && (
					<div className={cn(styles.filtersOokla, styles.controlBlock)}>
						<Tooltip
							content={tooltips[EControlsType.filtersOokla]}
							interactive
							isDisabled={controlsState[EControlsType.filtersOokla]}
							noWordBreak
						>
							<div
								data-id='icon-button'
								onClick={handleMapControlView(EControlsType.filtersOokla)}
							>
								<DynamicIconWrapper
									data-id='icon-button'
									icon={Icons.speedTest}
									isActive={controlsState[EControlsType.filtersOokla]}
									isDisabled={!!pinnedFilter && pinnedFilter !== EControlsType.filtersOokla}
								/>
								{controlsState[EControlsType.filtersOokla] && <FiltersOokla />}
							</div>
						</Tooltip>
					</div>
				)}
				<div className={cn(styles.options, styles.controlBlock)}>
					{!isAssetsMode && (
						<Tooltip
							content={tooltips[EControlsType.sectors]}
							interactive
							isDisabled={controlsState[EControlsType.sectors]}
							noWordBreak
						>
							<div
								data-id='icon-button'
								onClick={handleMapControlView(EControlsType.sectors)}
							>
								<DynamicIconWrapper
									data-id='icon-button'
									icon={Icons.sectorsIcon}
									isActive={controlsState[EControlsType.sectors] || isSectorsShown}
									isDisabled={!!pinnedFilter && pinnedFilter !== EControlsType.sectors}
								/>
								{controlsState[EControlsType.sectors] && <Sectors />}
							</div>
						</Tooltip>
					)}

					<Tooltip
						content={tooltips[EControlsType.filters]}
						interactive
						isDisabled={controlsState[EControlsType.filters]}
						noWordBreak
					>
						<div
							data-id='icon-button'
							onClick={handleMapControlView(EControlsType.filters)}
						>
							<DynamicIconWrapper
								data-id='icon-button'
								icon={isAssetsMode ? Icons.filter : Icons.sitesFilterIcon}
								isActive={controlsState[EControlsType.filters] || isFiltersApplied}
								isDisabled={!!pinnedFilter && pinnedFilter !== EControlsType.filters}
							/>
							{controlsState[EControlsType.filters] && <GeneralFilters type={EFilterType.attributes} />}
						</div>
					</Tooltip>
					{isOwnersOrVendorsFilterAvailable && (
						<Tooltip
							content={tooltips[EControlsType.owners]}
							interactive
							isDisabled={controlsState[EControlsType.owners]}
							noWordBreak
						>
							<div
								data-id='icon-button'
								onClick={handleMapControlView(EControlsType.owners)}
								onMouseEnter={prefetchAssetOwnersForFilter}
							>
								<DynamicIconWrapper
									data-id='icon-button'
									icon={isAssetsMode ? Icons.owner : Icons.vendors}
									isActive={controlsState[EControlsType.owners] || isOwnersFiltersApplied}
									isDisabled={!!pinnedFilter && pinnedFilter !== EControlsType.owners}
								/>
								{controlsState[EControlsType.owners] && <GeneralFilters type={EFilterType.owners} />}
							</div>
						</Tooltip>
					)}
				</div>
				{isKpiView &&
					<div className={cn(styles.settings, styles.controlBlock)}>
						<Tooltip
							content={tooltips[EControlsType.dashboardKpi]}
							interactive
							isDisabled={controlsState[EControlsType.dashboardKpi]}
							noWordBreak
						>
							<div
								data-id='icon-button'
								onClick={handleMapControlView(EControlsType.dashboardKpi)}
							>
								<DynamicIconWrapper
									data-id='icon-button'
									icon={Icons.chartsOnMap}
									isActive={controlsState[EControlsType.dashboardKpi]}
									isDisabled={!!pinnedFilter && pinnedFilter !== EControlsType.dashboardKpi}
								/>
							</div>
						</Tooltip>
					</div>
				}
				<div className={cn(styles.settings, styles.controlBlock)}>
					<Tooltip
						content={tooltips[EControlsType.mapSettings]}
						interactive
						isDisabled={controlsState[EControlsType.mapSettings]}
						noWordBreak
					>
						<div
							data-id='icon-button'
							onClick={handleMapControlView(EControlsType.mapSettings)}
						>
							<DynamicIconWrapper
								data-id='icon-button'
								icon={Icons.settings}
								isActive={controlsState[EControlsType.mapSettings]}
								isDisabled={!!pinnedFilter && pinnedFilter !== EControlsType.mapSettings}
							/>
							{controlsState[EControlsType.mapSettings] && <MapSettings />}
						</div>
					</Tooltip>
				</div>
			</div>
		) : null
	);
};

export default memo(RightSideControls);
